import * as d3 from "d3";
import { OrgChart } from "d3-org-chart";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { useEffect, useMemo, useRef } from "react";
import * as ReactDOMServer from "react-dom/server";
import { useHistory } from "react-router";
import { useRecoilValue } from "recoil";
import { useGetEmployeeHierarchyById } from "../../hooks/useGetEmployeeHierarchyById";
import EHOrgChartNode from "./EmpHierarchyOrgChartNode";

const EHOrgChart = ({ empData }) => {
  const d3Container = useRef(null);
  const chartRef = useRef(new OrgChart());
  const containerStyles = {
    width: "100%",
    height: "100%",
  };

  const userPermissions = useRecoilValue(permissionsSelector);
  // Employee Hierarchy chart data
  const { data: dataChart, isFetching: empDataChartFetching } =
    useGetEmployeeHierarchyById(empData?.employeeId || 0);

  const history = useHistory();

  //Redirection to employee for territory to district
  const toEmployee = (d) => {
    if (d !== undefined) {
      history.push({
        pathname: "/employeetab/employee",
        state: {
          employeeId: d.data.employeeId,
          prevEmployeeId: empData?.employeeId,
          action: "EmpOrgChart",
        },
      });
      window.location.reload();
    }
  };

  // Define the layout logic outside useEffect
  const createCustomLayout = (chart) => {
    const layout = chart.layoutBindings();
    return {
      ...layout,
      top: {
        ...layout.top,
        // Override compact mode link positioning
        linkCompactXStart: (node) => {
          const pairIndex = Math.floor(node.compactViewIndex / 2); // Group into pairs
          const offset = pairIndex % 3; // Group into 3 sets
          return node.x + offset * (node.width + 50); // Horizontal offset
        },
        linkCompactYStart: (node) => {
          const pairIndex = Math.floor(node.compactViewIndex / 2);
          return node.y + (pairIndex % 2) * 80; // Vertical offset
        },
      },
    };
  };

  const customLayout = useMemo(() => createCustomLayout(chartRef.current), []);

  useEffect(() => {
    try {
      if (!dataChart || !d3Container.current) return;
      const chart = chartRef.current;

      chart
        .container(d3Container.current)
        .data(dataChart?.filter((x) => x.parentId !== "0"))
        // Chart line color
        .linkUpdate(function () {
          d3.select(this).attr("stroke", "#ced4da").attr("stroke-width", 2);
        })
        .nodeWidth(() => {
          return 384;
        })
        .nodeHeight(() => {
          return 128;
        })
        .initialZoom(0.8)
        .childrenMargin((d) => 120)
        .compactMarginBetween((d) => 100)
        .compactMarginPair((d) => 90)
        .siblingsMargin((d) => 100)
        .nodeContent((node) => {
          return ReactDOMServer.renderToString(
            <EHOrgChartNode node={node} employeeId={empData?.employeeId} />
          );
        })
        .nodeUpdate(function (d) {
          d3.zoom().scaleExtent([1, 40]).on("zoom");
          //Redirection for territory to area
          d3.select(this)
            .select(".node_card_name")
            .on("click", () => {
              toEmployee(d);
            });
          d3.select(this)
            .select(".node_card_expand")
            .on("click", () => {
              toEmployee(d);
            });
        })
        .expandAll()
        .render();
    } catch (error) {
      console.log("ERROR", error);
    }
  }, [dataChart, userPermissions, d3Container, customLayout]);

  return empDataChartFetching ? (
    <>
      <div className="ml-7 border border-VividBlue">
        <span className="text-black ml-2 font-bold">LOADING...</span>
      </div>
    </>
  ) : dataChart?.filter((x) => x.parentId !== "0").length > 0 ? (
    <>
      <div className="mx-4 border border-VividBlue">
        <div style={containerStyles} ref={d3Container} className="relative" />
      </div>
    </>
  ) : (
    <>
      <div className="ml-7 border border-VividBlue">
        <span className="disclosureLabel ml-2">No Hierarchy Data</span>
      </div>
    </>
  );
};

export default EHOrgChart;
